import { useCallback, useRef } from 'react';
import { SectionId, FilterDraft, SectionDraft, UseSessionStorageDraftReturn } from '../types/filterDraft.types';

const FILTER_DRAFT_KEY = 'filter_draft';

function isKeyOf<T extends object>(obj: T, key: PropertyKey): key is keyof T {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

const StorageService = {
  read: (onError?: (error: unknown) => void): FilterDraft => {
    try {
      const item = sessionStorage.getItem(FILTER_DRAFT_KEY);
      if (!item) return {};
      const parsed: FilterDraft = JSON.parse(item);
      return parsed;
    } catch (error) {
      onError?.(error);
      return {};
    }
  },
  write: (data: FilterDraft, onError?: (error: unknown) => void): void => {
    try {
      if (Object.keys(data).length === 0) {
        sessionStorage.removeItem(FILTER_DRAFT_KEY);
        return;
      }
      sessionStorage.setItem(FILTER_DRAFT_KEY, JSON.stringify(data));
    } catch (error) {
      onError?.(error);
    }
  },
  remove: (onError?: (error: unknown) => void): void => {
    try {
      sessionStorage.removeItem(FILTER_DRAFT_KEY);
    } catch (error) {
      onError?.(error);
    }
  },
};

interface UseSessionStorageDraftProps {
  onError?: (error: unknown) => void;
}

export const useSessionStorageDraft = ({ onError }: UseSessionStorageDraftProps = {}): UseSessionStorageDraftReturn => {
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const handleError = useCallback((error: unknown) => {
    onErrorRef.current?.(error);
  }, []);

  const draftRef = useRef<FilterDraft | null>(null);

  if (draftRef.current === null) {
    draftRef.current = StorageService.read(handleError);
  }

  const getDraft = useCallback((): FilterDraft | null => {
    draftRef.current = StorageService.read(handleError);
    const current = draftRef.current;
    if (!current || Object.keys(current).length === 0) return null;
    return current;
  }, [handleError]);

  const getSectionDraft = useCallback((sectionId: SectionId): SectionDraft | null => {
    const current = draftRef.current;
    if (!current || !isKeyOf(current, sectionId)) return null;
    return current[sectionId] ?? null;
  }, []);

  const setSectionDraft = useCallback(
    (sectionId: SectionId, data: SectionDraft) => {
      if (!draftRef.current) draftRef.current = {};

      const currentSection = draftRef.current[sectionId];
      const currentJson = JSON.stringify(currentSection);
      const newJson = JSON.stringify(data);

      if (currentJson === newJson) return;

      draftRef.current = {
        ...draftRef.current,
        [sectionId]: data,
      };

      StorageService.write(draftRef.current, handleError);
    },
    [handleError],
  );

  const clearSectionDraft = useCallback(
    (sectionId: SectionId) => {
      if (!draftRef.current || !isKeyOf(draftRef.current, sectionId)) return;

      const { [sectionId]: removed, ...rest } = draftRef.current;
      draftRef.current = rest;

      StorageService.write(draftRef.current, handleError);
    },
    [handleError],
  );

  const clearAllDrafts = useCallback(() => {
    draftRef.current = {};
    StorageService.remove(handleError);
  }, [handleError]);

  const hasAnySectionWithData = useCallback((sectionIds: SectionId[]): boolean => {
    const current = draftRef.current;
    if (!current) return false;

    return sectionIds.some(id => {
      if (!isKeyOf(current, id)) return false;
      const section = current[id];
      return section && Object.keys(section).length > 0;
    });
  }, []);

  const getSectionsWithData = useCallback((): SectionId[] => {
    const current = draftRef.current;
    if (!current) return [];

    const keys = Object.keys(current);
    const activeSections: SectionId[] = [];

    for (const key of keys) {
      if (isKeyOf(current, key)) {
        const section = current[key];
        if (section && Object.keys(section).length > 0) {
          activeSections.push(key);
        }
      }
    }

    return activeSections;
  }, []);

  return {
    getDraft,
    getSectionDraft,
    setSectionDraft,
    clearSectionDraft,
    clearAllDrafts,
    hasAnySectionWithData,
    getSectionsWithData,
  };
};
